type KeyState = { down: boolean; pressed: boolean; released: boolean };

export class Input {
  private keys = new Map<string, KeyState>();
  private mouseX = 0;
  private mouseY = 0;
  private mouseDeltaX = 0;
  private mouseDeltaY = 0;
  private scrollY = 0;
  private readonly scrollAttenuation = 0.85;
  private readonly gamepadDeadzone = 0.15;
  
  // Virtual Joystick State (für Mobile)
  private virtualJoystickX = 0; // -1 bis 1
  private virtualJoystickY = 0; // -1 bis 1
  private virtualJoystickActive = false;

  constructor() {
    window.addEventListener('keydown', this.onKeyDown, { passive: true });
    window.addEventListener('keyup', this.onKeyUp, { passive: true });
    window.addEventListener('mousemove', this.onMouseMove, { passive: true });
    window.addEventListener('wheel', this.onWheel, { passive: true });
    window.addEventListener('blur', this.onBlur, { passive: true });
  }

  update() {
    this.keys.forEach((state) => {
      state.pressed = false;
      state.released = false;
    });

    this.mouseDeltaX = 0;
    this.mouseDeltaY = 0;
    this.scrollY *= this.scrollAttenuation;
  }

  isDown(code: string): boolean {
    return this.keys.get(code)?.down ?? false;
  }

  isPressed(code: string): boolean {
    return this.keys.get(code)?.pressed ?? false;
  }

  anyDown(codes: readonly string[]): boolean {
    return codes.some((code) => this.isDown(code));
  }

  anyPressed(codes: readonly string[]): boolean {
    return codes.some((code) => this.isPressed(code));
  }

  getMouseDelta() {
    return { x: this.mouseDeltaX, y: this.mouseDeltaY };
  }

  getMousePosition() {
    return { x: this.mouseX, y: this.mouseY };
  }

  getScrollY() {
    return this.scrollY;
  }

  isGamepadConnected(): boolean {
    return this.getPrimaryGamepad() !== null;
  }

  getSteerAxis(): number {
    const gp = this.getPrimaryGamepad();
    const gamepadSteer = gp ? this.applyAxisCurve(gp.axes[0] ?? 0) : 0;
    
    // Virtual Joystick hat Priorität wenn aktiv
    if (this.virtualJoystickActive) {
      return this.virtualJoystickX;
    }
    
    return gamepadSteer;
  }

  getThrottleAxis(): number {
    const gp = this.getPrimaryGamepad();
    if (!gp) return 0;

    const rt = gp.buttons[7]?.value ?? 0;
    const axis = gp.axes[5] ?? -1;
    const axis01 = (axis + 1) * 0.5;
    return Math.max(rt, axis01);
  }

  getBrakeAxis(): number {
    const gp = this.getPrimaryGamepad();
    if (!gp) return 0;

    const lt = gp.buttons[6]?.value ?? 0;
    const axis = gp.axes[2] ?? -1;
    const axis01 = (axis + 1) * 0.5;
    return Math.max(lt, axis01);
  }

  isGamepadButtonDown(index: number): boolean {
    const gp = this.getPrimaryGamepad();
    if (!gp) return false;
    return !!gp.buttons[index]?.pressed;
  }
  
  /**
   * Setzt Virtual Joystick State (für Mobile Touch-Controls)
   */
  setVirtualJoystick(x: number, y: number, active: boolean) {
    this.virtualJoystickX = x;
    this.virtualJoystickY = y;
    this.virtualJoystickActive = active;
  }
  
  /**
   * Gibt Virtual Joystick Y-Achse zurück (für Gas/Bremse in manchen Games)
   */
  getVirtualJoystickY(): number {
    return this.virtualJoystickY;
  }

  setVirtualKey(code: string, down: boolean) {
    this.onKey(code, down);
  }

  clear() {
    this.keys.forEach((state) => {
      state.down = false;
      state.pressed = false;
      state.released = false;
    });
  }

  dispose() {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('blur', this.onBlur);
  }

  private onKeyDown = (event: KeyboardEvent) => {
    this.onKey(event.code, true);
  };

  private onKeyUp = (event: KeyboardEvent) => {
    this.onKey(event.code, false);
  };

  private onBlur = () => {
    this.clear();
  };

  private onMouseMove = (event: MouseEvent) => {
    this.mouseDeltaX += event.movementX;
    this.mouseDeltaY += event.movementY;
    this.mouseX = event.clientX;
    this.mouseY = event.clientY;
  };

  private onWheel = (event: WheelEvent) => {
    this.scrollY += event.deltaY;
  };

  private onKey(code: string, down: boolean) {
    const state = this.keys.get(code) ?? { down: false, pressed: false, released: false };
    if (down && !state.down) state.pressed = true;
    if (!down && state.down) state.released = true;
    state.down = down;
    this.keys.set(code, state);
  }

  private getPrimaryGamepad(): Gamepad | null {
    if (!('getGamepads' in navigator)) return null;
    const pads = navigator.getGamepads();
    for (const pad of pads) {
      if (pad && pad.connected) return pad;
    }
    return null;
  }

  private applyAxisCurve(value: number): number {
    const abs = Math.abs(value);
    if (abs <= this.gamepadDeadzone) return 0;

    const normalized = (abs - this.gamepadDeadzone) / (1 - this.gamepadDeadzone);
    const curved = normalized * normalized;
    return Math.sign(value) * curved;
  }
}
